import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { WorkItemBase } from 'common/workitems/models/workitem-base-model';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { WorkItemsTrackingService } from 'common/workitems/workitems-tracking.service';
import { Component, OnInit, OnDestroy, Inject, ElementRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { WorkItemListService } from 'common/workitems/workitem-list.service';

const SCROLL_SMOOTH_ANIMATION_LENGTH = 300;

@Component({
  selector: 'app-work-item-lane-list',
  templateUrl: './work-item-lane-list.component.html',
  styleUrls: ['./work-item-lane-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WorkItemLaneListComponent implements OnInit, OnDestroy {
  public workItemFocusedSubscription: Subscription;
  public workItemTraverseLeftSubscription: Subscription;
  public workItemTraverseRightSubscription: Subscription;
  public workItemToggleExpandedModeSubscription: Subscription;
  public workItemToggleVisitorInformationSubscription: Subscription;

  constructor(private _workAreaManager: WorkAreaManager,
              private _workItemList: WorkItemListService,
              private _element: ElementRef,
              @Inject('velocityjs') private _velocityjs,
              private _workItemStateHandler: WorkItemStateHandler,
              private _rxEmitter: RxEmitter,
              private _workItemsTrackingService: WorkItemsTrackingService) {
  }

  ngOnInit(): void {
    this.workItemFocusedSubscription = this._workItemStateHandler.workItemFocused$.subscribe(focusedWorkItemId => {
      setTimeout(() => {
        this._scrollToLaneThenFocusIt(focusedWorkItemId);
        this._workItemsTrackingService.setVisiblePanelCount(this.getVisibleItemCount());
      }, 200);
    });

    this.workItemTraverseLeftSubscription = this._rxEmitter.subscribe(MessageBusEvents.TraverseWorkItemLeft, () => {
      this._workItemStateHandler.selectPreviousWorkItem();
    });

    this.workItemTraverseRightSubscription = this._rxEmitter.subscribe(MessageBusEvents.TraverseWorkItemRight, () => {
      this._workItemStateHandler.selectNextWorkItem();
    });

    this.workItemToggleExpandedModeSubscription = this._rxEmitter.subscribe(MessageBusEvents.ToggleExpandedMode, () => {
      this._workAreaManager.toggleExpandedMode();
    });

    this.workItemToggleVisitorInformationSubscription = this._rxEmitter.subscribe(MessageBusEvents.ToggleVisitorInformation, () => {
      const selectedWorkItem = this._workAreaManager.getSelectedWorkItem();

      if (selectedWorkItem && selectedWorkItem.id) {
        this._rxEmitter.send(`${MessageBusEvents.ToggleVisitorInformationByWorkItemId}${selectedWorkItem.id}`, null);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.workItemFocusedSubscription) {
      this._clearSubscription(this.workItemFocusedSubscription);
    }

    if (this.workItemTraverseLeftSubscription) {
      this._clearSubscription(this.workItemTraverseLeftSubscription);
    }

    if (this.workItemTraverseRightSubscription) {
      this._clearSubscription(this.workItemTraverseRightSubscription);
    }

    if (this.workItemToggleExpandedModeSubscription) {
      this._clearSubscription(this.workItemToggleExpandedModeSubscription);
    }

    if (this.workItemToggleVisitorInformationSubscription) {
      this._clearSubscription(this.workItemToggleVisitorInformationSubscription);
    }
  }

  get selectedWorkItem() {
    return this._workAreaManager.getSelectedWorkItem();
  }

  get workItemList() {
    return this._workAreaManager.openedWorkItemsList;
  }

  get isWorkItemListEmpty(): boolean {
    return this.workItemList.length === 0;
  }

  public getVisibleItemCount(): number {
    return this.workItemList.reduce((sum, item: WorkItemBase) => {
      if (this.getIsWorkItemInDom(item) && this.getIsWorkItemShown(item)) {
        return ++sum;
      } else {
        return sum;
      }
    }, 0);
  }

  public getIsWorkItemInDom(workItem: WorkItemBase): boolean {
    if (!workItem) {
      return false;
    }
    return (!this.isExpandedMode() && this.selectedWorkItem && this.selectedWorkItem.isCollapsible) || workItem.keepAlive || this.isWorkItemSelected(workItem.id);
  }

  public getIsWorkItemShown(workItem: WorkItemBase): boolean {
    if (!workItem) {
      return false;
    }
    return (!this.isExpandedMode() && this.selectedWorkItem && this.selectedWorkItem.isCollapsible && workItem.isCollapsible) || this.isWorkItemSelected(workItem.id);
  }

  public isPreviousWorkItemFromCurrentlySelectedOne(workItemId: string): boolean {
    return workItemId === this._workItemStateHandler.getPreviousWorkItemId();
  }

  public isFocused(workItemId: string): boolean {
    return this._workItemStateHandler.isFocused(workItemId);
  }

  public isNextWorkItemFromCurrentlySelectedOne(workItemId: string): boolean {
    return workItemId === this._workItemStateHandler.getNextWorkItemId();
  }

  public isExpandedMode(): boolean {
    return this._workAreaManager.isExpandedMode();
  }

  public openSettingsMenu(): void {
    this._rxEmitter.send(MessageBusEvents.OpenSettingsMenu, null);
  }

  public isWorkItemSelected(workItemId: string): boolean {
    if (!this.selectedWorkItem) {
      return false;
    }
    return this.selectedWorkItem.id === workItemId;
  }

  public selectWorkItem(workItem): void {
    this._workItemList.selectWorkitem(workItem);
  }

  private _scrollToLaneThenFocusIt(selectedWorkItemId: string): void {
    const laneElement = this._getWorkItemLaneByWorkItemId(selectedWorkItemId);

    if (!laneElement) {
      return;
    }

    const laneContainer = this._element.nativeElement.querySelector('.work-item-lane-container');
    const targetPosition = this._calculateScrollTarget(laneContainer, laneElement);

    let scrollResult: Promise<void>;
    if (!this._workAreaManager.isExpandedMode()) {
      // Scroll only in collapsed mode.
      // Animates scrollLeft to the offsetLeft of laneElement
      // The offset property adds to the target position
      scrollResult = this._velocityjs(laneElement, 'scroll', {
        axis: 'x',
        duration: SCROLL_SMOOTH_ANIMATION_LENGTH,
        container: laneContainer,
        offset: (targetPosition - laneElement.offsetLeft)
      });
    } else {
      scrollResult = Promise.resolve();
    }

    scrollResult.then(() => {
      // The focus must be set even in expanded mode, because otherwise keyboard navigation between work items does not work in Firefox.
      laneElement.focus();
    });
  }

  private _calculateScrollTarget(laneContainer, laneElement): number {
    const currentScrollPosition = laneContainer.scrollLeft;
    const leftDistance = laneElement.offsetLeft - currentScrollPosition;
    const rightDistance = (laneElement.offsetLeft + laneElement.offsetWidth) - currentScrollPosition;
    const visibleScrollSpace = laneContainer.offsetWidth;

    if (leftDistance < 0) {
      // Needs to slide in from left
      return laneElement.offsetLeft;
    } else if (rightDistance > visibleScrollSpace) {
      // Right edge is out of view
      const moveDistance = rightDistance - visibleScrollSpace;
      return currentScrollPosition + moveDistance;
    }
    // Element is in view
    return currentScrollPosition;
  }

  private _getWorkItemLaneByWorkItemId(workItemId: string) {
    return this._element.nativeElement.querySelector(`app-work-item-lane[data-workitem-id="${workItemId}"]`);
  }

  private _clearSubscription(subscription: Subscription): void {
    subscription.unsubscribe();
    subscription = null;
  }
}
